import {observer} from 'mobx-react-lite';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {ETooltip} from 'webapp-lib/Components';
import {ConditionalWrapper} from 'webapp-lib/utils';

import {assertUnreachable} from '_/utilities/types';

import {UserInitialsWithTooltip} from './UserInitialsWithTooltip';

const UDateAndUser = ({
  date: dateString,
  dateFormat,
  user,
  enableTimeDetailsTooltip,
}: {
  date?: string;
  dateFormat:
    | 'withTime'
    | 'withoutTime'
    | 'withoutTimeAndTwoDigitYear'
    | 'withTimeAndTwoDigitYear';
  user?: {
    initialLetters: string;
    displayName: string;
  };
  enableTimeDetailsTooltip?: boolean;
}): React.ReactElement => {
  const {t} = useTranslation('common');

  const getDateTranslation = (date: Date): string => {
    switch (dateFormat) {
      case 'withTime':
        return t('dateFormat.withTime', {date});
      case 'withoutTime':
        return t('dateFormat.withoutTime', {date});
      case 'withoutTimeAndTwoDigitYear':
        return t('dateFormat.withoutTimeAndTwoDigitYear', {date});
      case 'withTimeAndTwoDigitYear':
        return t('dateFormat.withTimeAndTwoDigitYear', {date});
      default:
        assertUnreachable(dateFormat);
    }
  };

  return (
    <span>
      {dateString ? (
        <ConditionalWrapper
          condition={Boolean(enableTimeDetailsTooltip)}
          wrapper={(child) => (
            <ETooltip
              title={t('dateFormat.withTimeAndSeconds', {
                date: new Date(dateString),
              })}
            >
              {child}
            </ETooltip>
          )}
        >
          <span>{getDateTranslation(new Date(dateString))}</span>
        </ConditionalWrapper>
      ) : null}
      {dateString && user ? <> ,&nbsp; </> : null}
      {user ? (
        <UserInitialsWithTooltip
          displayName={user.displayName}
          initials={user.initialLetters}
        />
      ) : null}
    </span>
  );
};

export const DateAndUser = observer(UDateAndUser);
